import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../axiosInstance";

// Create Coupon (admin)
export const createCoupon = createAsyncThunk(
  "coupon/createCoupon",
  async (couponData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/coupon/create-coupon`, couponData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// Get All Coupons (admin)
export const getAllCoupons = createAsyncThunk(
  "coupon/getAllCoupons",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/coupon/get-all-coupons`, { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// Get Single Coupon (admin)
export const getCoupon = createAsyncThunk(
  "coupon/getCoupon",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/coupon/get-coupon/${id}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// Update Coupon (admin)
export const updateCoupon = createAsyncThunk(
  "coupon/updateCoupon",
  async ({ id, couponData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/coupon/update-coupon/${id}`, couponData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// Delete Coupon (admin)
export const deleteCoupon = createAsyncThunk(
  "coupon/deleteCoupon",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/coupon/delete-coupon/${id}`);
      return { id, message: data.message };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// Validate Coupon (customer-facing)
export const validateCoupon = createAsyncThunk(
  "coupon/validateCoupon",
  async ({ code, cartTotal, userId, shippingMethod }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/coupon/validate`, {
        code,
        cartTotal,
        userId,
        shippingMethod,
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

/* ------------------------------------------------------------------ */
/* Slice                                                              */
/* ------------------------------------------------------------------ */

const initialState = {
  coupons: [],
  coupon: null,
  validatedCoupon: null,
  couponLoading: false,
  error: null,
  successMessage: null,
  pagination: {
    total: 0,
    page: 1,
    pages: 1,
  },
};

const couponSlice = createSlice({
  name: "coupon",
  initialState,
  reducers: {
    clearCouponErrors(state) {
      state.error = null;
    },
    clearCouponMessages(state) {
      state.successMessage = null;
    },
    resetCouponState() {
      return initialState;
    },
    clearValidatedCoupon(state) {
      state.validatedCoupon = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ---------- Create Coupon ---------- */
      .addCase(createCoupon.pending, (state) => {
        state.couponLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createCoupon.fulfilled, (state, action) => {
        state.couponLoading = false;
        state.successMessage = action.payload.message;
        state.coupons.unshift(action.payload.coupon);
      })
      .addCase(createCoupon.rejected, (state, action) => {
        state.couponLoading = false;
        state.error = action.payload;
      })

      /* ---------- Get All Coupons ---------- */
      .addCase(getAllCoupons.pending, (state) => {
        state.couponLoading = true;
        state.error = null;
      })
      .addCase(getAllCoupons.fulfilled, (state, action) => {
        state.couponLoading = false;
        state.coupons = action.payload.coupons;
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pages: action.payload.pages,
        };
      })
      .addCase(getAllCoupons.rejected, (state, action) => {
        state.couponLoading = false;
        state.error = action.payload;
      })

      /* ---------- Get Single Coupon ---------- */
      .addCase(getCoupon.pending, (state) => {
        state.couponLoading = true;
        state.error = null;
      })
      .addCase(getCoupon.fulfilled, (state, action) => {
        state.couponLoading = false;
        state.coupon = action.payload.coupon;
      })
      .addCase(getCoupon.rejected, (state, action) => {
        state.couponLoading = false;
        state.error = action.payload;
      })

      /* ---------- Update Coupon ---------- */
      .addCase(updateCoupon.pending, (state) => {
        state.couponLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateCoupon.fulfilled, (state, action) => {
        state.couponLoading = false;
        state.successMessage = action.payload.message;
        const idx = state.coupons.findIndex(
          (c) => c._id === action.payload.coupon._id,
        );
        if (idx !== -1) state.coupons[idx] = action.payload.coupon;
        if (state.coupon?._id === action.payload.coupon._id) {
          state.coupon = action.payload.coupon;
        }
      })
      .addCase(updateCoupon.rejected, (state, action) => {
        state.couponLoading = false;
        state.error = action.payload;
      })

      /* ---------- Delete Coupon ---------- */
      .addCase(deleteCoupon.pending, (state) => {
        state.couponLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteCoupon.fulfilled, (state, action) => {
        state.couponLoading = false;
        state.successMessage = action.payload.message;
        state.coupons = state.coupons.filter(
          (c) => c._id !== action.payload.id,
        );
        if (state.coupon?._id === action.payload.id) state.coupon = null;
      })
      .addCase(deleteCoupon.rejected, (state, action) => {
        state.couponLoading = false;
        state.error = action.payload;
      })

      /* ---------- Validate Coupon ---------- */
      .addCase(validateCoupon.pending, (state) => {
        state.couponLoading = true;
        state.error = null;
        state.validatedCoupon = null;
      })
      .addCase(validateCoupon.fulfilled, (state, action) => {
        state.couponLoading = false;
        state.validatedCoupon = action.payload.coupon;
      })
      .addCase(validateCoupon.rejected, (state, action) => {
        state.couponLoading = false;
        state.error = action.payload;
        state.validatedCoupon = null;
      });
  },
});

export const {
  clearCouponErrors,
  clearCouponMessages,
  resetCouponState,
  clearValidatedCoupon,
} = couponSlice.actions;

export default couponSlice.reducer;
